import { useRef, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { postAllData } from "../../service/ProductService";
import { UPDATE_PAYMENT_METHOD } from "../../config/ApiConstant";
import { Toast } from "primereact/toast";
import { useNavigate } from "react-router-dom";

const stripePromise = loadStripe(
  "pk_test_51SRnM1JmTxOyRUiYJh3oAfzabfOmcIqkaUcZCJz2SaqGW9xDGQXxrodQTcUjit0ocJDMBlimH0DaQAo6QqN0ZI7O00tlrR7vWs"
);

// ------------------------------
// Wrapper Component
// ------------------------------
export default function UpdatePaymentMethod({
  customerId,
  onSuccess,
  getApiCall,
}: any) {
  return (
    <Elements
      stripe={stripePromise}
      options={{
        appearance: { theme: "stripe" },
      }}
    >
      <PaymentForm
        customerId={customerId}
        onSuccess={onSuccess}
        getApiCall={getApiCall}
      />
    </Elements>
  );
}

// ------------------------------
// Payment Form Component
// ------------------------------
function PaymentForm({ customerId, onSuccess, getApiCall }: any) {
  const stripe: any = useStripe();
  const elements = useElements();
  const toast = useRef<any>(null);
  const navigate = useNavigate();

  const [loading, setLoading] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<any>();

  //  Single error object for all fields
  const [fieldErrors, setFieldErrors] = useState<any>({});

  //  One onChange handler for all Stripe fields
  const handleStripeChange = (event: any, field: string) => {
    setFieldErrors((prev: any) => ({
      ...prev,
      [field]: event?.error?.message || "",
    }));
  };

  const elementStyle = {
    style: {
      base: {
        fontSize: "16px",
        color: "#32325d",
        padding: "6px 12px",
        backgroundColor: "#fff",
        "::placeholder": { color: "#a0a0a0" },
      },
      invalid: { color: "#fa755a" },
    },
  };

  // ---------------------------------
  // Submit Card Details
  // ---------------------------------
  const onSubmitCardDetail = async (onSuccess: any) => {
    try {
      setLoading(true);
      setSubmitError(null);

      if (!stripe || !elements) {
        setSubmitError("Stripe has not loaded yet.");
        setLoading(false);
        return;
      }

      //  Block submit if any field has an error
      if (fieldErrors.cardNumber || fieldErrors.expiry || fieldErrors.cvc) {
        setLoading(false);
        return;
      }

      const cardNumberElement = elements.getElement(CardNumberElement);

      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: "card",
        card: cardNumberElement,
      });

      if (error) {
        setSubmitError(error.message || "Error static Error");
        setLoading(false);
        return;
      }

      const payload = {
        paymentMethodId: paymentMethod?.id,
        brand: paymentMethod?.card?.brand,
        exp_month: paymentMethod?.card?.exp_month,
        exp_year: paymentMethod?.card?.exp_year,
        last4: paymentMethod?.card?.last4,
      };

      const response = await postAllData(UPDATE_PAYMENT_METHOD, payload);

      if (response) {
        toast.current?.show({
          severity: "success",
          summary: "Added",
          detail: response?.message || "Success Static",
        });

        // setTimeout(() => navigate("/dashboard"), 500);
        localStorage.setItem("payment_details_added", "true");
        if (response) {
          setTimeout(() => {
            if (onSuccess) onSuccess(); // close modal in Dashboard
            getApiCall();
          }, 1000);
        }
      }
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.response?.data?.message || "Error static",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Toast ref={toast} />

      <div className="payment-wrapper">
        <p className="pb-2">
          <span style={{ fontWeight: 600 }}>Note:</span> Your card details are
          required only to cover the platform charge and enable access to all
          features.
        </p>
        <div className="payment-card">
          {/* <h3 className="payment-title">Add Card Details</h3> */}

          {/* CARD NUMBER */}
          <div className="field-group">
            <label className="field-label">
              Card Number <span className="required">*</span>
            </label>

            <div className="input-box">
              <CardNumberElement
                options={elementStyle}
                onChange={(e) => handleStripeChange(e, "cardNumber")}
              />
            </div>

            {fieldErrors.cardNumber && (
              <p className="field-error">{fieldErrors.cardNumber}</p>
            )}
          </div>

          {/* EXPIRY */}
          <div className="field-group">
            <label className="field-label">
              Expiry Date <span className="required">*</span>
            </label>

            <div className="input-box">
              <CardExpiryElement
                options={elementStyle}
                onChange={(e) => handleStripeChange(e, "expiry")}
              />
            </div>

            {fieldErrors.expiry && (
              <p className="field-error">{fieldErrors.expiry}</p>
            )}
          </div>

          {/* CVC */}
          <div className="field-group">
            <label className="field-label">
              CVC <span className="required">*</span>
            </label>

            <div className="input-box">
              <CardCvcElement
                options={elementStyle}
                onChange={(e) => handleStripeChange(e, "cvc")}
              />
            </div>

            {fieldErrors.cvc && (
              <p className="field-error">{fieldErrors.cvc}</p>
            )}
          </div>

          {submitError && <p className="error-msg">{submitError}</p>}

          <button
            onClick={() => onSubmitCardDetail(onSuccess)}
            className="submit-btn"
            disabled={loading}
          >
            {loading ? "Processing..." : "Save Card"}
          </button>
        </div>
      </div>
    </>
  );
}
